import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  Button,
  alpha,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, startOfDay, endOfDay, differenceInMinutes, subDays } from 'date-fns';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReplayIcon from '@mui/icons-material/Replay';
import { ScheduledCall, CallMetrics } from '../types';
import { callSchedulingApi } from '../services/api';
import AgentSelect from './AgentSelect';
import PhoneNumberSelect from './PhoneNumberSelect';

// Colors for charts
const CHART_COLORS = ['#2196f3', '#4caf50', '#f44336', '#ff9800', '#9c27b0', '#795548'];

interface DateRange {
  startDate: Date;
  endDate: Date;
}

const CallAnalytics: React.FC = () => {
  const [calls, setCalls] = useState<ScheduledCall[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    from_dt: null as Date | null,
    to_dt: null as Date | null,
    agent_id: '',
    from_number: '',
    to_number: '',
    is_reminder: '',
  });

  // Metrics state
  const [metrics, setMetrics] = useState({
    totalCalls: 0,
    completedCalls: 0,
    failedCalls: 0,
    averageCallDuration: 0,
    satisfactionScore: 0,
    automationStats: {
      totalTasks: 0,
      totalMessages: 0,
      totalActions: 0,
      successRate: 0,
    },
  });

  // Chart data state
  const [chartData, setChartData] = useState({
    callVolume: [] as any[],
    callDurations: [] as any[],
    satisfactionTrend: [] as any[],
    statusDistribution: [] as any[],
    actionsTaken: [] as any[],
  });

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    // Initialize date range from URL parameters
    const params = new URLSearchParams(window.location.search);
    return {
      startDate: params.get('start_date') ? new Date(String(params.get('start_date'))) : subDays(new Date(), 7),
      endDate: params.get('end_date') ? new Date(String(params.get('end_date'))) : new Date()
    };
  });

  // Update URL when date range changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    if (dateRange.startDate) {
      params.set('start_date', dateRange.startDate.toISOString());
    } else {
      params.delete('start_date');
    }
    
    if (dateRange.endDate) {
      params.set('end_date', dateRange.endDate.toISOString());
    } else {
      params.delete('end_date');
    }

    // Update URL without reloading
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [dateRange]);

  // Handle URL parameter changes
  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      setDateRange({
        startDate: params.get('start_date') ? new Date(String(params.get('start_date'))) : subDays(new Date(), 7),
        endDate: params.get('end_date') ? new Date(String(params.get('end_date'))) : new Date()
      });
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prepare filter parameters
      const filterParams: any = {};
      if (filters.status) filterParams.status = filters.status;
      if (filters.agent_id) filterParams.agent_id = filters.agent_id;
      if (filters.from_dt) filterParams.from_dt = format(filters.from_dt, "yyyy-MM-dd'T'HH:mm:ss'Z'");
      if (filters.to_dt) filterParams.to_dt = format(filters.to_dt, "yyyy-MM-dd'T'HH:mm:ss'Z'");
      if (filters.from_number) filterParams.from_number = filters.from_number;
      if (filters.to_number) filterParams.to_number = filters.to_number;

      const response = await callSchedulingApi.getScheduledCalls(filterParams);
      
      if (response && response.scheduled_calls) {
        const processedCalls = processCallsData(response.scheduled_calls);
        setCalls(processedCalls);
        calculateMetrics(processedCalls);
        generateChartData(processedCalls);
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const processCallsData = (calls: ScheduledCall[]): ScheduledCall[] => {
    return calls.map(call => {
      const processedCall = { ...call };
      
      if (typeof processedCall.metrics === 'string') {
        try {
          processedCall.metrics = JSON.parse(processedCall.metrics);
        } catch (err) {
          console.error('Error parsing metrics:', err);
          processedCall.metrics = null;
        }
      }
      
      return processedCall;
    });
  };

  const calculateMetrics = (calls: ScheduledCall[]) => {
    const completed = calls.filter(call => call.call_placed).length;
    const failed = calls.filter(call => call.call_attempted && !call.call_placed).length;
    
    let totalDuration = 0;
    let totalSatisfaction = 0;
    let satisfactionCount = 0;
    let totalTasks = 0;
    let totalMessages = 0;
    let totalActions = 0;
    let successfulAutomations = 0;
    let totalAutomations = 0;
    let callsWithDuration = 0;

    calls.forEach(call => {
      const metrics = call.metrics;
      if (metrics) {
        // Call duration
        if (metrics.call_duration) {
          const duration = parseDuration(metrics.call_duration);
          if (duration) {
            totalDuration += duration;
            callsWithDuration++;
          }
        }

        // Satisfaction score
        if (typeof metrics.caller_satisfaction === 'number' && !isNaN(metrics.caller_satisfaction)) {
          totalSatisfaction += metrics.caller_satisfaction;
          satisfactionCount++;
        }

        // Automation stats
        if (metrics.auto_tasks?.extracted_tasks) {
          totalTasks += metrics.auto_tasks.extracted_tasks.length;
          totalAutomations += metrics.auto_tasks.extracted_tasks.length;
          successfulAutomations += metrics.auto_tasks.api_results?.filter(r => r.success).length || 0;
        }
        if (metrics.auto_text_messages?.extracted_messages) {
          totalMessages += metrics.auto_text_messages.extracted_messages.length;
          totalAutomations += metrics.auto_text_messages.extracted_messages.length;
          successfulAutomations += metrics.auto_text_messages.api_results?.filter(r => 
            r.success === true || (r.success && typeof r.success === 'object' && r.success.success === true)
          ).length || 0;
        }
        if (metrics.tools_used) {
          totalActions += metrics.tools_used.length;
          totalAutomations += metrics.tools_used.length;
          successfulAutomations += metrics.tools_used.filter(t => 
            t.tool_result.toLowerCase().includes('success')
          ).length;
        }
      }
    });

    setMetrics({
      totalCalls: calls.length,
      completedCalls: completed,
      failedCalls: failed,
      averageCallDuration: callsWithDuration > 0 ? totalDuration / callsWithDuration : 0,
      satisfactionScore: satisfactionCount > 0 ? totalSatisfaction / satisfactionCount : 0,
      automationStats: {
        totalTasks,
        totalMessages,
        totalActions,
        successRate: totalAutomations > 0 ? (successfulAutomations / totalAutomations) * 100 : 0,
      },
    });
  };

  const generateChartData = (calls: ScheduledCall[]) => {
    // Call volume by date
    const volumeByDate = new Map();
    calls.forEach(call => {
      if (call.schedule_dt) {
        const date = format(new Date(call.schedule_dt), 'yyyy-MM-dd');
        volumeByDate.set(date, (volumeByDate.get(date) || 0) + 1);
      }
    });

    // Call durations
    const durations = calls
      .filter(call => call.metrics?.call_duration)
      .map(call => ({
        id: call.id,
        duration: parseDuration(call.metrics?.call_duration || '0:00') || 0,
      }))
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 10);

    // Satisfaction trend
    const satisfactionByDate = new Map();
    const satisfactionCounts = new Map();
    calls.forEach(call => {
      if (call.schedule_dt && call.metrics?.caller_satisfaction !== undefined) {
        const date = format(new Date(call.schedule_dt), 'yyyy-MM-dd');
        const currentTotal = satisfactionByDate.get(date) || 0;
        const currentCount = satisfactionCounts.get(date) || 0;
        satisfactionByDate.set(date, currentTotal + call.metrics.caller_satisfaction);
        satisfactionCounts.set(date, currentCount + 1);
      }
    });

    // Status distribution
    const statusCount = {
      completed: calls.filter(call => call.call_placed).length,
      failed: calls.filter(call => call.call_attempted && !call.call_placed).length,
      pending: calls.filter(call => !call.call_attempted).length,
    };

    // Actions taken distribution
    const actionCounts = new Map<string, { success: number; failed: number }>();

    calls.forEach(call => {
      const metrics = call.metrics;
      if (metrics) {
        // Count tasks
        if (metrics.auto_tasks?.extracted_tasks) {
          const successfulTasks = metrics.auto_tasks.api_results?.filter(r => r.success).length || 0;
          const failedTasks = metrics.auto_tasks.extracted_tasks.length - successfulTasks;
          actionCounts.set('Create Task', {
            success: (actionCounts.get('Create Task')?.success || 0) + successfulTasks,
            failed: (actionCounts.get('Create Task')?.failed || 0) + failedTasks
          });
        }

        // Count messages
        if (metrics.auto_text_messages?.extracted_messages) {
          const successfulMessages = metrics.auto_text_messages.api_results?.filter(r => 
            r.success === true || (r.success && typeof r.success === 'object' && r.success.success === true)
          ).length || 0;
          const failedMessages = metrics.auto_text_messages.extracted_messages.length - successfulMessages;
          actionCounts.set('Send Message', {
            success: (actionCounts.get('Send Message')?.success || 0) + successfulMessages,
            failed: (actionCounts.get('Send Message')?.failed || 0) + failedMessages
          });
        }

        // Count specific actions
        if (metrics.tools_used) {
          metrics.tools_used.forEach(tool => {
            const isSuccess = tool.tool_result.toLowerCase().includes('success');
            const actionName = tool.tool_name;
            const current = actionCounts.get(actionName) || { success: 0, failed: 0 };
            actionCounts.set(actionName, {
              success: current.success + (isSuccess ? 1 : 0),
              failed: current.failed + (isSuccess ? 0 : 1)
            });
          });
        }
      }
    });

    // Convert action counts to chart data format
    const actionsTaken = Array.from(actionCounts.entries()).flatMap(([name, counts]) => [
      { name: `${name} (Success)`, value: counts.success, color: '#4caf50' },
      ...(counts.failed > 0 ? [{ name: `${name} (Failed)`, value: counts.failed, color: '#f44336' }] : [])
    ]).filter(item => item.value > 0);

    setChartData({
      callVolume: Array.from(volumeByDate.entries()).map(([date, count]) => ({
        date,
        calls: count,
      })),
      callDurations: durations,
      satisfactionTrend: Array.from(satisfactionByDate.entries()).map(([date, total]) => ({
        date,
        satisfaction: total / (satisfactionCounts.get(date) || 1),
      })),
      statusDistribution: [
        { name: 'Completed', value: statusCount.completed, color: '#4caf50' },  // Green
        { name: 'Failed', value: statusCount.failed, color: '#f44336' },       // Red
        { name: 'Pending', value: statusCount.pending, color: '#ff9800' },     // Yellow/Orange
      ],
      actionsTaken,
    });
  };

  const parseDuration = (duration: string): number => {
    const parts = duration.split(':');
    if (parts.length === 2) {
      // Convert MM:SS format to fractional minutes
      const minutes = parseInt(parts[0]);
      const seconds = parseInt(parts[1]);
      return minutes + (seconds / 60);
    }
    return 0;
  };

  const formatDuration = (minutes: number): string => {
    const mins = Math.floor(minutes);
    const secs = Math.round((minutes - mins) * 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const handleFilterChange = (name: keyof typeof filters, value: any) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    const filteredCalls = calls.filter(call => {
      const config = typeof call.outbound_call_config === 'string' 
        ? JSON.parse(call.outbound_call_config) 
        : call.outbound_call_config;
      
      return (
        (!filters.status || call.call_attempted === (filters.status === 'attempted')) &&
          (!filters.from_dt || new Date(call.schedule_dt || '') >= filters.from_dt) &&
          (!filters.to_dt || new Date(call.schedule_dt || '') <= filters.to_dt) &&
          (!filters.agent_id || call.agent_id === parseInt(filters.agent_id)) &&
          (!filters.from_number || call.from_number === filters.from_number) &&
          (!filters.to_number || call.to_number === filters.to_number) &&
          (!filters.is_reminder || config.is_reminder === (filters.is_reminder === 'true'))
      );
    });
    
    setCalls(filteredCalls);
    calculateMetrics(filteredCalls);
    generateChartData(filteredCalls);
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      from_dt: null,
      to_dt: null,
      agent_id: '',
      from_number: '',
      to_number: '',
      is_reminder: '',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={3}>
        {/* Filters Section */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: alpha('#000', 0.08),
          }}
        >
          <Box 
            sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderBottom: filtersVisible ? `1px solid ${alpha('#000', 0.08)}` : 'none',
              cursor: 'pointer'
            }}
            onClick={() => setFiltersVisible(!filtersVisible)}
          >
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Filters
            </Typography>
            <IconButton size="small">
              {filtersVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          {filtersVisible && (
            <Box sx={{ p: 1.5 }}>
              <Stack spacing={1.5}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <AgentSelect
                      value={filters.agent_id ? parseInt(filters.agent_id.toString()) : null}
                      onChange={(value) => handleFilterChange('agent_id', value)}
                      label="Agent"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <DatePicker
                      label="From Date"
                      value={filters.from_dt}
                      onChange={(date) => handleFilterChange('from_dt', date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1,
                              backgroundColor: alpha('#000', 0.02),
                            },
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <DatePicker
                      label="To Date"
                      value={filters.to_dt}
                      onChange={(date) => handleFilterChange('to_dt', date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1,
                              backgroundColor: alpha('#000', 0.02),
                            },
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Reminder</InputLabel>
                      <Select
                        value={filters.is_reminder}
                        onChange={(e) => handleFilterChange('is_reminder', e.target.value)}
                        label="Reminder"
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Reminders Only</MenuItem>
                        <MenuItem value="false">Non-Reminders Only</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={resetFilters}
                        size="small"
                        sx={{
                          borderStyle: 'dashed',
                          color: 'text.secondary',
                          flex: 1,
                        }}
                      >
                        Reset
                      </Button>
                      <Button
                        variant="contained"
                        onClick={applyFilters}
                        startIcon={<RefreshIcon />}
                        size="small"
                        sx={{ flex: 1 }}
                      >
                        Update
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Key Metrics */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary">Total Calls</Typography>
              <Typography variant="h4">{metrics.totalCalls}</Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="success.main">
                  {((metrics.completedCalls / metrics.totalCalls) * 100).toFixed(1)}% Success Rate
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary">Average Duration</Typography>
              <Typography variant="h4">{formatDuration(metrics.averageCallDuration)}</Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Per completed call
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary">Satisfaction Score</Typography>
              <Typography variant="h4">{metrics.satisfactionScore.toFixed(1)}/10</Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Average rating (out of 10)
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary">Automation Success</Typography>
              <Typography variant="h4">{metrics.automationStats.successRate.toFixed(1)}%</Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {metrics.automationStats.totalTasks + metrics.automationStats.totalMessages + metrics.automationStats.totalActions} total actions
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
          {/* Call Volume Trend */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, height: '400px' }}>
              <Typography variant="h6" gutterBottom>Call Volume Trend</Typography>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={chartData.callVolume}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="calls" stroke="#2196f3" name="Number of Calls" />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Status Distribution */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '400px' }}>
              <Typography variant="h6" gutterBottom>Call Status Distribution</Typography>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={chartData.statusDistribution}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {chartData.statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Satisfaction Trend */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '400px' }}>
              <Typography variant="h6" gutterBottom>Satisfaction Trend</Typography>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={chartData.satisfactionTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 10]} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="satisfaction" stroke="#4caf50" name="Satisfaction Score" />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Actions Taken Distribution */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '400px' }}>
              <Typography variant="h6" gutterBottom>Actions Taken Distribution</Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={chartData.actionsTaken} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={150} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Count">
                    {chartData.actionsTaken.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

export default CallAnalytics; 